import * as Sentry from '@sentry/react'

/**
 * Logs errors to console and captures them with Sentry.
 * Provides enhanced handling for Axios errors by extracting relevant information.
 *
 * @param {Object} params - The parameters object
 * @param {string} [params.message] - Custom error message (optional)
 * @param {Error|Object} params.error - The error object
 * @param {Object} [params.extraData] - Additional context data for debugging (optional)
 * @returns {Error} - The error that was captured by Sentry
 */
export const notifySentry = ({ message, error, extraData }) => {
  // Determine the message to display
  const errorMessage = message || error?.message || 'An unknown error occurred'

  // Enhanced information for console logging
  let axiosErrorData = {}

  // Check if it's an Axios error
  const isAxiosError = error?.isAxiosError || (error?.response && error?.config) || error?.request

  if (isAxiosError) {
    if (error?.response) {
      // If we have a response, extract useful information from it
      const { data, headers: { warning } = {}, status } = error.response
      axiosErrorData = {
        status,
        data,
        warning,
        message: error?.message,
      }
    } else if (error?.request) {
      // Request was made but no response received
      const request = error.request || {}
      axiosErrorData = {
        responseURL: request.responseURL,
        method: error.config?.method,
        timeout: error.config?.timeout,
      }
    }
  }

  // Log to console for Sentry to pick up as breadcrumbs
  console.error(
    'message:',
    errorMessage,
    '\nerror:',
    error,
    '\naxios data:',
    JSON.stringify(axiosErrorData),
    '\nextra data:',
    JSON.stringify(extraData)
  )

  // Create a custom error to ensure a consistent error object with the message
  const customError = new Error(errorMessage)

  // Add original error as cause for context
  if (error) {
    customError.cause = error

    // Copy stack trace from original error if available
    if (error.stack) {
      customError.originalStack = error.stack
    }

    // Copy axios-specific properties for more context
    if (isAxiosError) {
      customError.axiosError = true
      customError.axiosData = axiosErrorData
    }
  }

  // Capture in Sentry
  Sentry.captureException(customError)
  return customError
}
